import { Spin } from "antd";
import i18n from "i18next";
import { type FC, useRef } from "react";
import { useCallback, useEffect, useState } from "react";

import { updateWatchedFieldsUtils } from "src/app/features/Site/SiteAndSiteGroupUtils";
import { useSiteGroupLoadQuery } from "src/app/features/SiteGroup/services/server/SiteGroupApi";
import { FormManager } from "../../../../../components/Form/FormManager";
import { useFormSubmitCompletion } from "../../../../../components/Form/hooks/useFormSubmitCompletion";
import { useLoadFormDefinitionQuery } from "../../../../../components/Form/services/server/serverApi";
import { FormLayoutType } from "../../../../../components/Form/types";
import type { FormProps } from "../../../../../components/Form/types/FormProps";
import { InheritedFieldChangeConfirmationModal } from "../../../../../components/Modal/InheritedFieldChangeConfirmationModal";
import { ResourceName } from "../../../../../core/router/config/ResourceName";
import { BASE_PATH, FORM_ACTION } from "../../../../../core/router/types/routerTypes";
import type { ID } from "../../../../../core/types/fe";
import { isTheSameByDeepCompare } from "../../../../../core/utils/utilFunctions";
import { useSiteSaveMutation } from "../../services/server/SiteApi";
import type { SiteEntity } from "../../types/SiteEntity";

export const SiteCreateForm: FC<FormProps> = ({ runSubmit, formAction }) => {
    const { data: formDefinition, isFetching: configIsFetching } = useLoadFormDefinitionQuery("site");

    const [formData, setFormData] = useState<SiteEntity>({} as SiteEntity);
    const completeSubmit = useFormSubmitCompletion(formAction, `${BASE_PATH}/${ResourceName.SITES}`);
    const [saveFormData, { isLoading: saveFormIsInProgress }] = useSiteSaveMutation();
    const prevParentUnitIdRef = useRef<ID | undefined>(undefined);
    const prevChildProductProfileIdRef = useRef<ID | undefined>(undefined);
    const pendingFormDataRef = useRef<SiteEntity | null>(null);
    const formDataRef = useRef(formData);
    const [parentUnitId, setParentUnitId] = useState<ID | undefined>(undefined);
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

    const applyUpdatedData = useCallback((updatedData: SiteEntity) => {
        formDataRef.current = updatedData;

        if (updatedData.childProductProfileId !== prevChildProductProfileIdRef.current) {
            prevChildProductProfileIdRef.current = updatedData.childProductProfileId;
            setFormData(updatedData);
        }

        if (updatedData.parentUnit !== prevParentUnitIdRef.current) {
            setParentUnitId(updatedData.parentUnit);
            setFormData(updatedData);

            if (updatedData.parentUnit === undefined) {
                prevParentUnitIdRef.current = undefined;
            }
        }
    }, []);

    const onChangeCallback = useCallback(
        (newData: SiteEntity) => {
            const updatedData = { ...newData, statusCheckReadOnly: formDataRef.current?.statusCheckReadOnly };

            const hasParentUnitChange = updatedData.parentUnit !== prevParentUnitIdRef.current;
            const hasChildProductProfileChange = updatedData.childProductProfileId !== prevChildProductProfileIdRef.current;

            if ((hasParentUnitChange && updatedData.parentUnit !== undefined) || hasChildProductProfileChange) {
                pendingFormDataRef.current = updatedData;
                setIsConfirmModalOpen(true);
                setFormData({ ...formDataRef.current });
                return;
            }

            applyUpdatedData(updatedData);
        },
        [applyUpdatedData],
    );

    const onConfirmParentUnitChange = useCallback(() => {
        const pendingData = pendingFormDataRef.current;
        pendingFormDataRef.current = null;
        setIsConfirmModalOpen(false);
        if (pendingData) {
            applyUpdatedData(pendingData);
        }
    }, [applyUpdatedData]);

    const onCancelParentUnitChange = useCallback(() => {
        pendingFormDataRef.current = null;
        setIsConfirmModalOpen(false);
        setFormData({ ...formDataRef.current });
    }, []);

    // HANDLE WATCH FIELDS
    const { data: siteGroupEntity } = useSiteGroupLoadQuery(
        { entityId: parentUnitId ? `${parentUnitId}` : undefined },
        { skip: !parentUnitId },
    );

    const updateWatchedFields = useCallback(() => {
        const currentParentUnitId = formDataRef.current?.parentUnit;

        if (!currentParentUnitId || !siteGroupEntity || String(siteGroupEntity.id) !== String(currentParentUnitId)) return null;

        prevParentUnitIdRef.current = currentParentUnitId;
        if (!formDataRef.current) return null;

        return updateWatchedFieldsUtils(siteGroupEntity, formDataRef.current);
    }, [siteGroupEntity]);

    useEffect(() => {
        if (!siteGroupEntity || !parentUnitId || String(siteGroupEntity.id) !== String(parentUnitId)) {
            return;
        }

        const prevParentUnitId = prevParentUnitIdRef.current;

        if (parentUnitId !== prevParentUnitId) {
            let newFormData = updateWatchedFields();
            if (newFormData && !isTheSameByDeepCompare(formDataRef.current, newFormData)) {
                setFormData(newFormData);
                formDataRef.current = newFormData;
                newFormData = null;
            }
        }
    }, [parentUnitId, updateWatchedFields, siteGroupEntity]);

    const onValidatedCallback = useCallback(
        async (valid: boolean) => {
            const currentFormData = formDataRef.current;
            if (!valid || !currentFormData) {
                return;
            }

            const result = await saveFormData({ data: currentFormData });

            const hasError = "error" in result;
            if (!hasError) {
                completeSubmit(
                    i18n.t("common.formSaveSuccess", { id: result.data.id, resource: "Site" }),
                    `${BASE_PATH}/${ResourceName.SITES}/${FORM_ACTION.EDIT}/${result.data.id}`,
                );
            }
        },
        [saveFormData, completeSubmit],
    );

    return (
        <Spin spinning={saveFormIsInProgress || configIsFetching}>
            {formDefinition?.formConfiguration && (
                <FormManager<SiteEntity>
                    name="SiteCreateForm"
                    formData={formData}
                    formLayout={FormLayoutType.TABS}
                    formDefinition={formDefinition.formConfiguration}
                    onChange={onChangeCallback}
                    shouldValidate={runSubmit}
                    onValidated={onValidatedCallback}
                />
            )}
            <InheritedFieldChangeConfirmationModal
                open={isConfirmModalOpen}
                onConfirm={onConfirmParentUnitChange}
                onCancel={onCancelParentUnitChange}
            />
        </Spin>
    );
};
